import {
  EReputationAnalysisStatus,
  EReputationCategory,
  EReputationStatus,
} from "src/core/constants/eReputation";
import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel } from "src/core/constants/risk";
import { FolderFileQuery } from "src/core/gql/graphql";
import { MappedEntity } from "src/core/types/entity";
import { findCountryLabel } from "src/core/utils/country";
import { formatIsoToDate } from "src/core/utils/date";

type ReputationEntryInput =
  | {
      providerResult: boolean;
      analysis?: { name: string } | null;
    }
  | undefined;

/**
 * Mapping des alertes PEP et sanctions.
 * Celles-ci sont définies au niveau des stakeholders.
 * Si l'analyse n'a pas été effectuée, alors la propriété "eReputation" est undefined.
 */
export const mapEntities = (
  folderFile: FolderFileQuery,
  countryWithRiskOptions: IRiskOption[]
): MappedEntity[] => {
  const rawStakeholders = folderFile.folderFile?.fileCustomerFolder?.stakeholders ?? [];

  return rawStakeholders.map((stakeholder): MappedEntity => {
    const id = stakeholder.entity.id;

    const creationDate = folderFile.folderFile?.adminStatus?.creation
      ? formatIsoToDate(folderFile.folderFile?.adminStatus?.creation)
      : undefined;

    const eReputation = stakeholder.entity.eReputation;

    const pepEntry = eReputation?.entries.find(
      (entry) => entry.category.code === EReputationCategory.PEP
    );
    const sanctionEntry = eReputation?.entries.find(
      (entry) => entry.category.code === EReputationCategory.SANCTION
    );

    const pepStatus = mapReputationStatus(pepEntry);
    const sanctionStatus = mapReputationStatus(sanctionEntry);

    const riskLevel = determineRiskLevel(pepStatus, sanctionStatus);

    const isPPH = !stakeholder.isFolderCustomer;
    let firstName: string | undefined;
    let lastName: string | undefined;
    let name: string | undefined;
    let birthDate: string | undefined;
    let citizenshipCountryCode: string | undefined;
    let registrationCountryCode: string | undefined;

    const fieldMap: Record<string, (value: string) => void> = {
      firstName: (v) => (firstName = v),
      lastName: (v) => (lastName = v),
      name: (v) => (name = v),
      birthDate: (v) => (birthDate = formatIsoToDate(v)),
      citizenshipCountryCode: (v) =>
        (citizenshipCountryCode = findCountryLabel(v, countryWithRiskOptions)),
      registrationCountryCode: (v) =>
        (registrationCountryCode = findCountryLabel(v, countryWithRiskOptions)),
    };

    stakeholder.inputValues.forEach((val) => {
      const code = val.inputField?.fieldCode;
      const value = val.stringValue;
      if (code && value && fieldMap[code]) {
        fieldMap[code](value);
      }
    });

    return {
      id,
      creationDate,
      riskLevel,
      isPPH,
      firstName,
      lastName,
      name,
      birthDate,
      citizenshipCountryCode,
      registrationCountryCode,
      pepStatus,
      sanctionStatus,
    };
  });
};

/**
 * Mapping des statuts des alertes PEP et sanctions.
 */
export const mapReputationStatus = (entry: ReputationEntryInput): EReputationStatus => {
  if (!entry) return EReputationStatus.NO_ANALYSIS;

  if (entry.providerResult === true) {
    if (!entry.analysis) return EReputationStatus.POTENTIAL;
    if (entry.analysis.name === EReputationAnalysisStatus.FAUX_POSITIF)
      return EReputationStatus.FALSE_POSITIVE;
    if (entry.analysis.name === EReputationAnalysisStatus.X) return EReputationStatus.CONFIRMED;
  } else {
    if (!entry.analysis || entry.analysis.name === EReputationAnalysisStatus.RAS) {
      return EReputationStatus.NO_ALERT;
    }
    if (entry.analysis.name === EReputationAnalysisStatus.X) return EReputationStatus.CONFIRMED;
  }

  return EReputationStatus.NO_ANALYSIS;
};

/**
 * Détermination du niveau de risque en fonction des statuts des alertes PEP et sanctions.
 * - Si l'alerte est potentielle, alors on considère le risque comme non défini (pastille grise).
 * - Si l'alerte est confirmée, alors on considère le risque comme élevé (pastille rouge).
 * - S'il n'y a aucune alerte, alors on considère le risque comme faible (pastille verte).
 * - Si l'analyse n'a pas été effectuée, alors on considère le risque comme non défini (pastille grise).
 * - Si l'alerte est un faux positif, alors on considère le risque comme faible (pastille verte).
 */
export const determineRiskLevel = (
  pep: EReputationStatus,
  sanction: EReputationStatus
): CustomRiskLevel => {
  if (pep === EReputationStatus.CONFIRMED || sanction === EReputationStatus.CONFIRMED)
    return CustomRiskLevel.HIGH;
  if (pep === EReputationStatus.POTENTIAL || sanction === EReputationStatus.POTENTIAL)
    return CustomRiskLevel.UNDEFINED;
  if (
    pep === EReputationStatus.NO_ALERT ||
    sanction === EReputationStatus.NO_ALERT ||
    pep === EReputationStatus.FALSE_POSITIVE ||
    sanction === EReputationStatus.FALSE_POSITIVE
  )
    return CustomRiskLevel.LOW;

  return CustomRiskLevel.UNDEFINED;
};
